/**
 * Contract Investors Section Component
 * Manages multiple investors for a contract with capital allocations
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatNumber } from '../../utils/validation';
import ConfirmDialog from '../common/ConfirmDialog';
import StakeholderAutocomplete from '../common/StakeholderAutocomplete';
import PriorContractAutocomplete from './PriorContractAutocomplete';

// Format number with space thousand separator
const formatWithSpaces = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return '0.00';
  return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

// Currency input: shows formatted when blurred, raw when focused
function CurrencyInput({ value, onChange, label, helperText, disabled, sx, ...props }) {
  const [focused, setFocused] = useState(false);
  const showFormatted = !focused || disabled;
  const rawValue = (() => {
    const num = parseFloat(value);
    return isNaN(num) ? '' : String(num);
  })();

  return (
    <TextField
      {...props}
      label={label}
      fullWidth
      value={showFormatted ? formatWithSpaces(value) : rawValue}
      onChange={disabled ? undefined : (e) => onChange(e.target.value.replace(/\s/g, ''))}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      type={showFormatted ? 'text' : 'number'}
      inputProps={showFormatted ? {} : { step: 0.01, min: 0 }}
      InputLabelProps={{ shrink: true }}
      helperText={helperText}
      disabled={disabled}
      sx={sx}
    />
  );
}

function ContractInvestorsSection({
  contractId,
  investors = [],
  availableInvestors = [],
  onAddInvestor,
  onUpdateInvestor,
  onRemoveInvestor,
  onInvestorCreated,
  onPriorContractSelected,
  onUpdateInvestorLTR,
  disabled = false,
  currencyCode = 'ZAR',
  countries = [],
  depositDate = null,
  rolledFromContractId = null,
  rolledFromReference = null,
  rolledFromUserReference = null,
  requireLikelihood = false,
}) {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInvestor, setEditingInvestor] = useState(null);
  const [formData, setFormData] = useState({
    investor_id: '',
    new_funds_amount: '',
    rolled_funds_amount: 0,
    rolled_from_contract_id: null,
    likelihood_to_roll: '',
  });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

  // Calculate totals
  const totalCapital = investors.reduce((sum, inv) => sum + parseFloat(inv.capital_amount || 0), 0);

  const handleOpenDialog = (investor = null) => {
    if (investor) {
      setEditingInvestor(investor);
      setFormData({
        investor_id: investor.investor_id,
        new_funds_amount: investor.new_funds_amount || '',
        rolled_funds_amount: investor.rolled_funds_amount || 0,
        rolled_from_contract_id: rolledFromContractId || null,
        likelihood_to_roll: investor.likelihood_to_roll || '',
      });
    } else {
      setEditingInvestor(null);
      setFormData({
        investor_id: '',
        new_funds_amount: '',
        rolled_funds_amount: 0,
        rolled_from_contract_id: null,
        likelihood_to_roll: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingInvestor(null);
    setFormData({
      investor_id: '',
      new_funds_amount: '',
      rolled_funds_amount: 0,
      rolled_from_contract_id: null,
      likelihood_to_roll: '',
    });
  };

  const handleSubmit = () => {
    const newFunds = parseFloat(formData.new_funds_amount) || 0;
    const rolledFunds = parseFloat(formData.rolled_funds_amount) || 0;
    const capitalAmount = newFunds + rolledFunds;

    if (!formData.investor_id || capitalAmount <= 0) {
      return;
    }

    if (editingInvestor) {
      onUpdateInvestor(editingInvestor.investor_id, capitalAmount, newFunds, rolledFunds, formData.likelihood_to_roll || null);
    } else {
      // Get selected investor details
      const selectedInvestor = availableInvestors.find(i => i.id === parseInt(formData.investor_id));
      onAddInvestor({
        investor_id: parseInt(formData.investor_id),
        investor_name: selectedInvestor?.name || 'Unknown',
        capital_amount: capitalAmount,
        new_funds_amount: newFunds,
        rolled_funds_amount: rolledFunds,
        wht_rate: selectedInvestor?.wht_rate || 0,
        likelihood_to_roll: formData.likelihood_to_roll || null,
      });
    }
    handleCloseDialog();
  };

  const handleRemove = (investorId) => {
    setConfirmDialog({
      open: true,
      title: 'Remove Investor',
      message: 'Are you sure you want to remove this investor?',
      onConfirm: () => {
        onRemoveInvestor(investorId);
        setConfirmDialog(prev => ({ ...prev, open: false }));
      },
    });
  };

  // Filter out already added investors
  const getAvailableInvestorsForSelect = () => {
    const addedIds = investors.map(i => i.investor_id);
    if (editingInvestor) {
      // When editing, include the current investor
      return availableInvestors;
    }
    return availableInvestors.filter(i => !addedIds.includes(i.id));
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon color="primary" />
            <Typography variant="h6">
              Investors ({investors.length})
            </Typography>
          </Box>
          {!disabled && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Investor
            </Button>
          )}
        </Box>

        {investors.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
            <Typography color="textSecondary">
              No investors added yet. Click "Add Investor" to allocate capital to investors.
            </Typography>
          </Paper>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Investor</TableCell>
                    {rolledFromContractId && <TableCell sx={{ whiteSpace: 'nowrap' }}>Rolled From</TableCell>}
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>New Funds</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Rolled Funds</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Total Capital</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>%</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>WHT Rate</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>Gross-Up</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>LTR</TableCell>
                    {!disabled && <TableCell align="center">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {investors.map((investor) => {
                    const percentage = totalCapital > 0
                      ? (parseFloat(investor.capital_amount) / totalCapital * 100).toFixed(2)
                      : 0;

                    return (
                      <TableRow key={investor.investor_id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="body2" fontWeight="medium">
                              {investor.investor_name}
                            </Typography>
                          </Box>
                        </TableCell>
                        {rolledFromContractId && (
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {parseFloat(investor.rolled_funds_amount || 0) > 0 ? (
                              <Typography
                                variant="body2"
                                color="primary"
                                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => navigate(`/contracts/${rolledFromContractId}`)}
                              >
                                {rolledFromReference}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                        )}
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2">
                            {currencyCode} {formatNumber(investor.new_funds_amount || 0, 2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2">
                            {currencyCode} {formatNumber(investor.rolled_funds_amount || 0, 2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2" fontWeight="bold">
                            {currencyCode} {formatNumber(investor.capital_amount, 2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {percentage}%
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {investor.wht_rate ? `${parseFloat(investor.wht_rate).toFixed(2)}%` : '0%'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={investor.wht_gross_up ? 'Yes' : 'No'}
                            color={investor.wht_gross_up ? 'info' : 'default'}
                            size="small"
                            variant={investor.wht_gross_up ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell>
                          {onUpdateInvestorLTR ? (
                            <TextField
                              select
                              size="small"
                              value={investor.likelihood_to_roll || ''}
                              onChange={(e) => onUpdateInvestorLTR(investor.investor_id, e.target.value)}
                              sx={{ minWidth: 120 }}
                            >
                              <MenuItem value=""><em>None</em></MenuItem>
                              <MenuItem value="Likely">Likely</MenuItem>
                              <MenuItem value="Middle">Possible</MenuItem>
                              <MenuItem value="Not likely">Not likely</MenuItem>
                            </TextField>
                          ) : (
                            <Typography
                              variant="body2"
                              color={requireLikelihood && !investor.likelihood_to_roll ? 'error' : 'textPrimary'}
                            >
                              {investor.likelihood_to_roll === 'Middle'
                                ? 'Possible'
                                : (investor.likelihood_to_roll || (requireLikelihood ? 'Required' : '\u2014'))}
                            </Typography>
                          )}
                        </TableCell>
                        {!disabled && (
                          <TableCell align="center">
                            <Tooltip title="Edit allocation">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(investor)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove investor">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemove(investor.investor_id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                  {/* Summary footer row */}
                  <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                    <TableCell>
                      <Typography variant="subtitle2" color="primary" fontWeight="bold">
                        {investors.length} Investor{investors.length !== 1 ? 's' : ''}
                      </Typography>
                    </TableCell>
                    {rolledFromContractId && <TableCell />}
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" color="primary" fontWeight="bold">
                        {currencyCode} {formatNumber(investors.reduce((s, i) => s + parseFloat(i.new_funds_amount || 0), 0), 2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" color="primary" fontWeight="bold">
                        {currencyCode} {formatNumber(investors.reduce((s, i) => s + parseFloat(i.rolled_funds_amount || 0), 0), 2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" color="primary" fontWeight="bold">
                        {currencyCode} {formatNumber(totalCapital, 2)}
                      </Typography>
                    </TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    {!disabled && <TableCell />}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingInvestor ? 'Edit Investor Allocation' : 'Add Investor'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid size={12}>
                <StakeholderAutocomplete
                  stakeholderType="investor"
                  options={getAvailableInvestorsForSelect()}
                  value={formData.investor_id}
                  onChange={(id) => setFormData(prev => ({ ...prev, investor_id: id }))}
                  onStakeholderCreated={(newInvestor) => {
                    if (onInvestorCreated) onInvestorCreated(newInvestor);
                    setFormData(prev => ({ ...prev, investor_id: newInvestor.id }));
                  }}
                  label="Investor *"
                  disabled={!!editingInvestor}
                  countries={countries}
                />
              </Grid>
              <Grid size={4}>
                <CurrencyInput
                  label="New Funds *"
                  value={formData.new_funds_amount}
                  onChange={(val) => setFormData(prev => ({ ...prev, new_funds_amount: val }))}
                  helperText={`Fresh capital (${currencyCode})`}
                />
              </Grid>
              <Grid size={4}>
                <CurrencyInput
                  label="Rolled Funds"
                  value={formData.rolled_funds_amount}
                  onChange={(val) => setFormData(prev => ({ ...prev, rolled_funds_amount: val }))}
                  helperText={`Rolled (${currencyCode})`}
                />
              </Grid>
              <Grid size={4}>
                <CurrencyInput
                  label="Total Capital"
                  value={(parseFloat(formData.new_funds_amount) || 0) + (parseFloat(formData.rolled_funds_amount) || 0) || ''}
                  disabled
                  helperText="New + Rolled"
                  sx={{ '& .MuiInputBase-input.Mui-disabled': { fontWeight: 'bold', WebkitTextFillColor: 'rgba(0,0,0,0.87)' } }}
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  select
                  label={requireLikelihood ? 'Likelihood to Roll *' : 'Likelihood to Roll'}
                  fullWidth
                  value={formData.likelihood_to_roll}
                  onChange={(e) => setFormData(prev => ({ ...prev, likelihood_to_roll: e.target.value }))}
                  error={requireLikelihood && !formData.likelihood_to_roll}
                  helperText={
                    requireLikelihood && !formData.likelihood_to_roll
                      ? 'Likelihood to Roll is required'
                      : 'Expected behavior at maturity'
                  }
                >
                  <MenuItem value="">
                    <em>Not specified</em>
                  </MenuItem>
                  <MenuItem value="Likely">Likely</MenuItem>
                  <MenuItem value="Middle">Possible</MenuItem>
                  <MenuItem value="Not likely">Not likely</MenuItem>
                </TextField>
              </Grid>
              {parseFloat(formData.rolled_funds_amount) > 0 && (
                <Grid size={12}>
                  <PriorContractAutocomplete
                    value={formData.rolled_from_contract_id || null}
                    onChange={(id, contractObj) => {
                      setFormData(prev => ({ ...prev, rolled_from_contract_id: id }));
                      if (onPriorContractSelected) onPriorContractSelected(id, contractObj);
                    }}
                    excludeId={contractId}
                    rolledFundsAmount={formData.rolled_funds_amount}
                    currencyCode={currencyCode}
                    depositDate={depositDate}
                    investorId={formData.investor_id}
                    investorName={availableInvestors.find(i => i.id === parseInt(formData.investor_id))?.name}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={
                !formData.investor_id
                || ((parseFloat(formData.new_funds_amount) || 0) + (parseFloat(formData.rolled_funds_amount) || 0)) <= 0
                || (parseFloat(formData.rolled_funds_amount) > 0 && !formData.rolled_from_contract_id)
              }
            >
              {editingInvestor ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Confirm"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
      />
    </Card>
  );
}

export default ContractInvestorsSection;
